import { CheckIcon, CircleCheckIcon, CopyIcon, GiftIcon } from 'lucide-react';
import { useState } from 'react';
import SaleSection from '@/partials/sale/sale-section';
import { ShareButton } from '@/shared/components/ui/buttons/share-button';
import { Progress } from '@/shared/components/ui/progress';
import links from '@/config/links.config';
import { paths } from '@/config/paths';
import { BonusThresholdMetric } from '@/types/api/bonus';
import { Referral, ReferralCode, ReferralCodeStatus, Sale, SaleStatus } from '@/types/api/sale';
import { cn } from '@/utils/shared/Utils';
import notify from '@/utils/shared/notify';
import { bonusAmountText, bonusValueText, formatKronor, thresholdText } from '@/utils/sale/bonus';
import { useReferralCode } from '../api/get-referral-code';

interface ReferralCardProps {
  sale: Sale;
}

/** "Tipsa en kompis!": the group's code to share, and how the groups it brought in are doing. */
export function ReferralCard({ sale }: ReferralCardProps) {
  // Only an active sale can share its code.
  const { data: code } = useReferralCode({ saleId: sale.id, enabled: sale.status === SaleStatus.Active });

  // Hidden while loading, for an ended sale, and when no referral program is running.
  if (sale.status !== SaleStatus.Active || !code) return null;

  return <ReferralCodeSection code={code} />;
}

function ReferralCodeSection({ code }: { code: ReferralCode }) {
  const commission = formatKronor(code.commission.amount, code.commission.currency);
  const expired = code.validUntil != null && new Date(code.validUntil) <= new Date();
  const full = code.maxUses != null && code.useCount >= code.maxUses;
  const usable = code.status === ReferralCodeStatus.Active && !expired && !full;
  const link = `${links.minasidor}${paths.start.getHref(code.code)}`;

  return (
    <SaleSection className="xl:col-span-2" bodyClassName="space-y-4">
      <div className="flex items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-violet-500/10 text-violet-500 dark:text-violet-400">
          <GiftIcon size={18} aria-hidden="true" />
        </span>
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Tipsa en kompis!</h2>
      </div>

      <div className="space-y-1.5">
        <div className="grid grid-cols-2 gap-2">
          <Amount label="Ni får" value={commission} />
          <Amount label="De får" value={bonusAmountText(code.bonus)} />
        </div>
        {code.bonus.threshold && (
          <p className="text-center text-xs text-gray-500 dark:text-gray-400">
            {thresholdText(code.bonus.threshold, 'de')}
          </p>
        )}
      </div>

      <CodeBox code={code.code} status={usable ? null : full ? 'Förbrukad' : 'Har gått ut'} />

      {usable && (
        <ShareButton
          className="w-full"
          data={{
            title: 'Tipsa en kompis',
            text: `Starta en försäljning på Piggo med koden ${code.code} så får ni ${bonusValueText(code.bonus)}!`,
            url: link,
          }}
        >
          Dela länk
        </ShareButton>
      )}

      <div className="text-xs text-gray-500 dark:text-gray-400">
        {code.maxUses == null ? `${code.useCount} använda` : `${code.useCount} av ${code.maxUses} använda`}
        {code.validUntil && ` · t.o.m. ${new Date(code.validUntil).toLocaleDateString('sv-SE')}`}
      </div>

      {code.referrals.length > 0 && (
        <ul className="space-y-2.5 border-t border-gray-100 pt-3 dark:border-gray-700/60">
          {code.referrals.map((referral, index) => (
            <ReferralRow key={index} label={`Grupp ${index + 1}`} referral={referral} commission={commission} />
          ))}
        </ul>
      )}
    </SaleSection>
  );
}

/** The whole box copies the code. With a `status` the code can't be used, so it's shown struck through. */
function CodeBox({ code, status }: { code: string; status: string | null }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      notify.error('Kunde inte kopiera koden');
    }
  };

  if (status) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed px-3 py-2 opacity-60">
        <span className="font-mono text-base font-semibold tracking-wider text-gray-800 line-through dark:text-gray-100">
          {code}
        </span>
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{status}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? 'Kopierad' : `Kopiera ${code}`}
      className="flex w-full items-center gap-3 rounded-lg border border-dashed border-violet-500/40 bg-violet-500/5 px-3 py-2 text-start transition-colors hover:bg-violet-500/10 dark:bg-violet-500/10 dark:hover:bg-violet-500/15"
    >
      {copied ? (
        <CheckIcon size={16} className="shrink-0 text-emerald-500" aria-hidden="true" />
      ) : (
        <CopyIcon size={16} className="shrink-0 text-violet-500 dark:text-violet-400" aria-hidden="true" />
      )}
      <span className="font-mono text-base font-semibold tracking-wider text-gray-800 dark:text-gray-100">{code}</span>
    </button>
  );
}

function Amount({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-gray-50 px-3 py-2 text-center dark:bg-gray-900/40">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-lg font-bold text-gray-800 dark:text-gray-100">{value}</div>
    </div>
  );
}

function ReferralRow({ label, referral, commission }: { label: string; referral: Referral; commission: string }) {
  const { progress, qualified, commissionPaidAt } = referral;
  const format = (value: number) =>
    progress?.metric === BonusThresholdMetric.Revenue ? formatKronor(value) : value.toLocaleString('sv-SE');

  return (
    <li className="flex items-center gap-3 text-sm">
      <span className="w-14 shrink-0 font-medium text-gray-700 dark:text-gray-200">{label}</span>
      {qualified ? (
        <span className="flex items-center gap-1.5 font-medium text-green-700 dark:text-green-400">
          <CircleCheckIcon size={14} aria-hidden="true" />
          {/* No Tipsbelöning when they reached the threshold after this sale had ended. */}
          {commissionPaidAt ? `+${commission}` : 'Klar'}
        </span>
      ) : progress ? (
        <>
          <Progress
            value={Math.min(100, (progress.current / progress.target) * 100)}
            className="h-1.5 grow bg-gray-100 dark:bg-gray-700/60"
          />
          <span className="shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
            {format(progress.current)}/{format(progress.target)}
          </span>
        </>
      ) : (
        <span className="text-xs text-gray-500 dark:text-gray-400">Startad</span>
      )}
    </li>
  );
}
